import { useEffect, useState } from "react";
import { StyledAICampaign } from "../CreateCompaign/AICampaign.styles";
import Form from '../../molecules/Form';
import Button from '../../molecules/Button';
import BotMessage from './BotMessage';
import ProcessingSteps from './ProcessingSteps';
import { StyledUserMessage } from './NewOnboarding.styles';
import Toast from "../../molecules/Toast";
import useOfferForm from "../CreateCompaign/useOfferForm";
import { languageOptions } from "../../Constant";
import { makeItem, personalizationOptions, toneOptions } from "../CreateCompaign/offerFormConfig";
import {
    CompanyIdentityFields,
    OfferDetailsFields,
    OutreachPreferenceFields,
} from "../CreateCompaign/OfferFormSections";

const EMBEDDED_EMAIL_LENGTH = '120';
const EMBEDDED_TONE = 'friendly';
const EMBEDDED_LANGUAGE = 'en';
const EMBEDDED_GREETING = 'Hello {{first_name}}';

const CONTINUE_WITH_AI_REPLY = [
    'Great.',
    'I now understand your business.',
    "Add a few final details below and I'll create your targeting, messaging and campaigns.",
];

const AUTO_FILL_LOADER_STEPS = [
    'Company Info',
    'Product Details',
    'Pain Points',
    'Finding Hidden Opportunities',
    'Offer Creation',
];

const EmbeddedOfferForm = ({ onComplete }) => {
    const offerForm = useOfferForm({ defaultEmailLength: EMBEDDED_EMAIL_LENGTH });
    const [detailsRevealed, setDetailsRevealed] = useState(false);
    const [showAutoFillLoader, setShowAutoFillLoader] = useState(false);
    const [manualEntry, setManualEntry] = useState(false);

    useEffect(() => {
        if (!detailsRevealed || showAutoFillLoader) return undefined;
        const applyDefaults = setTimeout(() => {
            offerForm.form.setFieldsValue({
                select_tone_from_dropdown: toneOptions.find(option => option.value === EMBEDDED_TONE),
                language_from_dropdown: languageOptions.find(option => option.value === EMBEDDED_LANGUAGE),
            });
        }, 0);
        return () => clearTimeout(applyDefaults);
    }, [detailsRevealed, showAutoFillLoader, offerForm.form]);

    const handleContinueWithAi = () => {
        if (!offerForm.canAutoFill) return;
        setDetailsRevealed(true);
        setShowAutoFillLoader(true);
        offerForm.autoFillFields({ notifySuccess: false });
    };

    const applyEmbeddedDefaults = () => {
        const personalizationDefaults = personalizationOptions.reduce((defaults, option) => {
            defaults[option.name] = true;
            return defaults;
        }, {});
        offerForm.form.setFieldsValue(personalizationDefaults);
        offerForm.setAllowedGreetings([makeItem({ value: EMBEDDED_GREETING })]);
        setShowAutoFillLoader(false);
    };

    // No website to scrape: skip auto-fill and reveal the same manual details form
    // (led by "What does your company do?") shown when auto-fill fails.
    const handleNoWebsite = () => {
        setManualEntry(true);
        setDetailsRevealed(true);
        applyEmbeddedDefaults();
    };

    const handleSubmit = () => {
        const hasAllowedGreeting = offerForm.allowedGreetings.some(item => item.value?.replace(/<[^>]*>/g, '').trim());
        if (!hasAllowedGreeting) {
            Toast({ type: 'error', message: 'At least one allowed greeting is required.' });
            return;
        }
        onComplete?.(offerForm.buildPayload());
    };

    return (
        <StyledAICampaign $embedded>
            <div className="content-holder">
                <Form form={offerForm.form} onSubmit={handleSubmit} onTouched={offerForm.handleFormChange}>
                    <div className="fields-wrapper">
                        <CompanyIdentityFields hideWebsite={manualEntry} hideCompany={!manualEntry} />
                        {detailsRevealed && (
                            <>
                                <StyledUserMessage>
                                    <span className="bubble">Continue</span>
                                </StyledUserMessage>
                                {showAutoFillLoader ? (
                                    <div className="aligned-bot-message">
                                        <ProcessingSteps
                                            title="Getting information from the website."
                                            steps={AUTO_FILL_LOADER_STEPS}
                                            loading={offerForm.autoFillLoading}
                                            onComplete={applyEmbeddedDefaults}
                                        />
                                    </div>
                                ) : (
                                    <>
                                        {offerForm.autoFillFailed || manualEntry ? (
                                            <OfferDetailsFields offerForm={offerForm} />
                                        ) : (
                                            <div className="aligned-bot-message">
                                                <BotMessage messages={CONTINUE_WITH_AI_REPLY} />
                                            </div>
                                        )}
                                        <OutreachPreferenceFields offerForm={offerForm} hideCoachingPoints />
                                    </>
                                )}
                            </>
                        )}
                    </div>
                    <div className="embedded-actions">
                        {detailsRevealed ? (
                            !showAutoFillLoader && (
                                <Button newStyle rounded variant="darkBlue" width="155px" type="submit">
                                    Continue
                                </Button>
                            )
                        ) : (
                            <>
                                <Button
                                    newStyle
                                    rounded
                                    variant="outline"
                                    width="190px"
                                    type="button"
                                    onClick={handleNoWebsite}>
                                    I don&apos;t have a website
                                </Button>
                                <Button
                                    newStyle
                                    rounded
                                    variant="darkBlue"
                                    width="155px"
                                    type="button"
                                    loader={offerForm.autoFillLoading}
                                    disabled={!offerForm.canAutoFill || offerForm.autoFillLoading}
                                    onClick={handleContinueWithAi}>
                                    Continue
                                </Button>
                            </>
                        )}
                    </div>
                </Form>
            </div>
        </StyledAICampaign>
    );
};

export default EmbeddedOfferForm;
